import { AlertCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import Button from '@/shared/components/ui/Button';

export type ConfirmModalVariant = 'danger' | 'primary' | 'success';

interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  variant?: ConfirmModalVariant;
  loading?: boolean;
  /** Extra input under the message, e.g. a reason */
  children?: ReactNode;
  /** Keeps the confirm button off, e.g. until a required input is filled */
  confirmDisabled?: boolean;
  onConfirm?: () => void;
  onClose?: () => void;
}

const VARIANT = {
  danger: { icon: 'text-[#FF3B30] bg-[#FF3B30]/10', button: 'btn-fill-red' },
  primary: { icon: 'text-[#0069A8] bg-[#0069A8]/10', button: 'btn-fill-blue' },
  success: { icon: 'text-bg-success bg-bg-success/10', button: 'btn-fill-success text-white' },
};

/** Ya / Kembali confirmation before an action (Batalkan, Request Jemput, ...). */
const ConfirmModal = ({
  open,
  title,
  message,
  confirmLabel,
  variant = 'danger',
  loading,
  children,
  confirmDisabled,
  onConfirm,
  onClose,
}: ConfirmModalProps) => {
  if (!open) return null;
  const style = VARIANT[variant];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Kembali"
        className="absolute inset-0 bg-black/50 cursor-default"
        disabled={loading}
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-[320px] rounded-xl bg-white p-6 flex flex-col items-center gap-3 text-center">
        <div
          className={cn('w-14 h-14 rounded-[12px] flex items-center justify-center', style.icon)}
        >
          <AlertCircle size={32} />
        </div>
        <p className="title1bold">{title}</p>
        <p className="body1 text-text-grey">{message}</p>
        {children && <div className="w-full text-left">{children}</div>}
        <div className="w-full flex flex-col gap-3 mt-2">
          <Button
            className={cn(style.button, 'w-full')}
            disabled={loading || confirmDisabled}
            onClick={onConfirm}
          >
            <span>{confirmLabel}</span>
          </Button>
          <Button className="btn-fill-white-db w-full" disabled={loading} onClick={onClose}>
            <span>Kembali</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
